import { useEffect, useRef, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ZoomIn, ZoomOut, Maximize2, Filter, X, HelpCircle, ChevronDown, RotateCcw } from "lucide-react";

interface NetworkNode {
  id: string;
  name: string;
  role: string;
  riskScore: number;
  centrality: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  clusterId?: string;
}

interface NetworkEdge {
  source: string;
  target: string;
  type: string;
  strength: number;
}

interface NodeDetail {
  node: NetworkNode;
  connections: { name: string; type: string; strength: number }[];
}

const CONNECTION_TYPES = [
  { value: "all", label: "All Connections" },
  { value: "supervisor", label: "Supervisor" },
  { value: "working_with", label: "Working With" },
  { value: "corroboration", label: "Corroboration" },
  { value: "task_shared", label: "Shared Task" },
  { value: "same_shift", label: "Same Shift" },
  { value: "same_location", label: "Same Location" },
  { value: "same_team", label: "Same Team" },
  { value: "reported", label: "Reported" },
];

const TERM_EXPLANATIONS = [
  {
    term: "Champion",
    description: "Workers with high positive influence in the network. They have many connections and consistently demonstrate safe behaviors. Champions are ideal candidates for peer mentoring programs.",
  },
  {
    term: "Bridge",
    description: "Workers who connect different teams or clusters. They can spread safety behaviors across groups but also risk spreading unsafe behaviors if they adopt them.",
  },
  {
    term: "Learner",
    description: "Workers who are connected to the network but haven't established leadership roles yet. They are influenced by their peers and can adopt behaviors from those around them.",
  },
  {
    term: "Isolate",
    description: "Workers with few or no network connections. They may miss peer influence for safety adoption and require direct interventions rather than network-based approaches.",
  },
  {
    term: "Centrality",
    description: "A measure of how connected and influential a worker is in the network. Higher centrality means more connections and greater potential to influence (or be influenced by) others.",
  },
  {
    term: "Supervisor Connection",
    description: "Direct reporting relationship between a worker and their supervisor. Shown as solid lines on the map.",
  },
  {
    term: "Working With",
    description: "Workers who have indicated they work together during their shift check-in. Shown as dashed lines.",
  },
  {
    term: "Corroboration",
    description: "Workers who have confirmed seeing the same hazard. This strengthens both the report validity and the network connection between workers.",
  },
  {
    term: "Same Shift / Same Location",
    description: "Workers who share the same work schedule or physical location. These contextual connections indicate potential for behavior influence.",
  },
  {
    term: "Cluster",
    description: "A group of workers who work closely together (same task, location, or shift). Risky behaviors can spread within clusters through peer influence.",
  },
];

interface NetworkVisualizationProps {
  siteId?: string;
}

export const NetworkVisualization = ({ siteId }: NetworkVisualizationProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<NetworkNode[]>([]);
  const [edges, setEdges] = useState<NetworkEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<NodeDetail | null>(null);
  const [zoom, setZoom] = useState(1);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const animationRef = useRef<number>();
  
  // Filter states
  const [connectionTypeFilter, setConnectionTypeFilter] = useState<string>("all");
  const [workerFilter, setWorkerFilter] = useState<string>("all");

  useEffect(() => {
    fetchNetworkData();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [siteId]);

  const fetchNetworkData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      // Get all workers with network data - filtered by site if provided
      let workersQuery = supabase
        .from("worker_profiles")
        .select("id, display_name, network_role, current_risk_score, network_centrality, cluster_id")
        .eq("company_id", profile.company_id);

      if (siteId) {
        workersQuery = workersQuery.eq("site_id", siteId);
      }

      const { data: workers } = await workersQuery;

      // Get worker IDs for filtering connections
      const workerIds = workers?.map(w => w.id) || [];

      // Get connections - only between workers in the filtered set
      let connectionsQuery = supabase
        .from("worker_network_connections")
        .select("*")
        .eq("company_id", profile.company_id);

      const { data: connections } = await connectionsQuery;

      // Filter connections to only include those between workers in our set
      const filteredConnections = (connections || []).filter(c => 
        workerIds.includes(c.worker_a) && workerIds.includes(c.worker_b)
      );

      if (!workers || workers.length === 0) {
        setLoading(false);
        return;
      }

      // Initialize nodes with random positions in a circle
      const centerX = 300;
      const centerY = 200;
      const radius = 150;

      const networkNodes: NetworkNode[] = workers.map((w, i) => {
        const angle = (2 * Math.PI * i) / workers.length;
        return {
          id: w.id,
          name: w.display_name || "Unknown",
          role: w.network_role || "learner",
          riskScore: w.current_risk_score || 0,
          centrality: w.network_centrality || 0,
          clusterId: w.cluster_id,
          x: centerX + radius * Math.cos(angle) + (Math.random() - 0.5) * 50,
          y: centerY + radius * Math.sin(angle) + (Math.random() - 0.5) * 50,
          vx: 0,
          vy: 0,
        };
      });

      const networkEdges: NetworkEdge[] = (filteredConnections || []).map((c) => ({
        source: c.worker_a,
        target: c.worker_b,
        type: c.connection_type,
        strength: c.connection_strength || 1,
      }));

      setNodes(networkNodes);
      setEdges(networkEdges);
      setLoading(false);

      // Start force simulation
      startSimulation(networkNodes, networkEdges);
    } catch (error) {
      console.error("Error fetching network data:", error);
      setLoading(false);
    }
  };

  // Get list of workers for dropdown
  const workerOptions = useMemo(() => {
    return [
      { value: "all", label: "All Workers" },
      ...nodes.map((n) => ({ value: n.id, label: n.name })).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [nodes]);

  // Compute filtered data based on filters
  const { filteredNodes, filteredEdges } = useMemo(() => {
    let fEdges = [...edges];
    let fNodes = [...nodes];

    // Filter by connection type first
    if (connectionTypeFilter !== "all") {
      fEdges = fEdges.filter((e) => e.type === connectionTypeFilter);
      
      // Only show nodes that have at least one connection of this type
      const nodesWithConnection = new Set<string>();
      fEdges.forEach((e) => {
        nodesWithConnection.add(e.source);
        nodesWithConnection.add(e.target);
      });
      fNodes = fNodes.filter((n) => nodesWithConnection.has(n.id));
    }

    // Filter by specific worker - show this worker and their connections
    if (workerFilter !== "all") {
      const selectedWorkerId = workerFilter;
      
      // Get edges involving the selected worker (from already filtered edges)
      fEdges = fEdges.filter(
        (e) => e.source === selectedWorkerId || e.target === selectedWorkerId
      );
      
      // Get nodes: selected worker + their connections
      const connectedIds = new Set<string>([selectedWorkerId]);
      fEdges.forEach((e) => {
        connectedIds.add(e.source);
        connectedIds.add(e.target);
      });
      
      fNodes = fNodes.filter((n) => connectedIds.has(n.id));
    }

    return { filteredNodes: fNodes, filteredEdges: fEdges };
  }, [nodes, edges, connectionTypeFilter, workerFilter]);

  const clearFilters = () => {
    setConnectionTypeFilter("all");
    setWorkerFilter("all");
  };

  const hasActiveFilters = connectionTypeFilter !== "all" || workerFilter !== "all";

  const startSimulation = (initialNodes: NetworkNode[], initialEdges: NetworkEdge[]) => {
    let simulationNodes = [...initialNodes];
    const iterations = 100;
    let currentIteration = 0;

    const simulate = () => {
      if (currentIteration >= iterations) {
        setNodes([...simulationNodes]);
        return;
      }

      // Apply forces
      const alpha = 1 - currentIteration / iterations;

      // Repulsion between all nodes
      for (let i = 0; i < simulationNodes.length; i++) {
        for (let j = i + 1; j < simulationNodes.length; j++) {
          const dx = simulationNodes[j].x - simulationNodes[i].x;
          const dy = simulationNodes[j].y - simulationNodes[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = (100 / (dist * dist)) * alpha;

          simulationNodes[i].vx -= (dx / dist) * force;
          simulationNodes[i].vy -= (dy / dist) * force;
          simulationNodes[j].vx += (dx / dist) * force;
          simulationNodes[j].vy += (dy / dist) * force;
        }
      }

      // Attraction along edges
      initialEdges.forEach((edge) => {
        const sourceNode = simulationNodes.find((n) => n.id === edge.source);
        const targetNode = simulationNodes.find((n) => n.id === edge.target);
        if (sourceNode && targetNode) {
          const dx = targetNode.x - sourceNode.x;
          const dy = targetNode.y - sourceNode.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = (dist - 80) * 0.01 * alpha * edge.strength;

          sourceNode.vx += (dx / dist) * force;
          sourceNode.vy += (dy / dist) * force;
          targetNode.vx -= (dx / dist) * force;
          targetNode.vy -= (dy / dist) * force;
        }
      });

      // Center gravity
      simulationNodes.forEach((node) => {
        node.vx += (300 - node.x) * 0.001 * alpha;
        node.vy += (200 - node.y) * 0.001 * alpha;

        // Apply velocity with damping
        node.x += node.vx * 0.5;
        node.y += node.vy * 0.5;
        node.vx *= 0.9;
        node.vy *= 0.9;

        // Keep in bounds
        node.x = Math.max(30, Math.min(570, node.x));
        node.y = Math.max(30, Math.min(370, node.y));
      });

      currentIteration++;
      setNodes([...simulationNodes]);
      animationRef.current = requestAnimationFrame(simulate);
    };

    simulate();
  };

  const getRoleColor = (role: string, isHighRisk: boolean = false) => {
    if (isHighRisk) return "#ef4444"; // Red for high risk
    switch (role) {
      case "champion":
        return "#22c55e"; // Green
      case "bridge":
        return "#3b82f6"; // Blue
      case "learner":
        return "#f59e0b"; // Yellow/Orange
      case "isolate":
        return "#ef4444"; // Red
      default:
        return "#6b7280"; // Gray
    }
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't trigger click if we were dragging
    if (isDragging) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX - rect.left) * scaleX - panOffset.x) / zoom;
    const y = ((e.clientY - rect.top) * scaleY - panOffset.y) / zoom;

    // Find clicked node - increased click radius for easier selection
    const clickedNode = filteredNodes.find((node) => {
      const dx = node.x - x;
      const dy = node.y - y;
      const baseRadius = node.role === "champion" ? 18 : 12;
      const clickRadius = baseRadius + 15; // Extra padding for easier clicking
      return Math.sqrt(dx * dx + dy * dy) < clickRadius;
    });

    if (clickedNode) {
      // Get connections for this node (from all edges, not filtered)
      const nodeConnections = edges
        .filter((e) => e.source === clickedNode.id || e.target === clickedNode.id)
        .map((e) => {
          const otherId = e.source === clickedNode.id ? e.target : e.source;
          const otherNode = nodes.find((n) => n.id === otherId);
          return {
            name: otherNode?.name || "Unknown",
            type: e.type,
            strength: e.strength,
          };
        });

      setSelectedNode({ node: clickedNode, connections: nodeConnections });
    }
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(false);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Handle dragging
    if (e.buttons === 1) {
      setIsDragging(true);
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
      canvas.style.cursor = "grabbing";
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.clientX - rect.left) * scaleX - panOffset.x) / zoom;
    const y = ((e.clientY - rect.top) * scaleY - panOffset.y) / zoom;

    const hovered = filteredNodes.find((node) => {
      const dx = node.x - x;
      const dy = node.y - y;
      const baseRadius = node.role === "champion" ? 18 : 12;
      const hoverRadius = baseRadius + 15;
      return Math.sqrt(dx * dx + dy * dy) < hoverRadius;
    });

    setHoveredNode(hovered?.id || null);
    canvas.style.cursor = hovered ? "pointer" : "grab";
  };

  const handleCanvasMouseUp = () => {
    setTimeout(() => setIsDragging(false), 10);
  };

  const handleCanvasMouseLeave = () => {
    setIsDragging(false);
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      try {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error("Failed to enter fullscreen:", err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error("Failed to exit fullscreen:", err);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Draw network on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || filteredNodes.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoom, zoom);

    // Draw edges
    filteredEdges.forEach((edge) => {
      const sourceNode = filteredNodes.find((n) => n.id === edge.source);
      const targetNode = filteredNodes.find((n) => n.id === edge.target);
      if (sourceNode && targetNode) {
        ctx.beginPath();
        ctx.moveTo(sourceNode.x, sourceNode.y);
        ctx.lineTo(targetNode.x, targetNode.y);
        ctx.strokeStyle = hoveredNode === edge.source || hoveredNode === edge.target
          ? "#3b82f6"
          : "#d1d5db";
        ctx.lineWidth = Math.min(edge.strength, 3);
        ctx.setLineDash(edge.type === "supervisor" ? [] : [5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    });

    // Draw nodes
    filteredNodes.forEach((node) => {
      const isHighlighted = hoveredNode === node.id || workerFilter === node.id;
      const radius = node.role === "champion" ? 18 : 12;
      const color = getRoleColor(node.role, node.riskScore > 60);

      ctx.beginPath();

      if (node.role === "champion") {
        // Draw star shape for champions
        const spikes = 5;
        const outerRadius = radius;
        const innerRadius = radius / 2;
        for (let i = 0; i < spikes * 2; i++) {
          const r = i % 2 === 0 ? outerRadius : innerRadius;
          const angle = (Math.PI / 2) + (i * Math.PI) / spikes;
          const x = node.x + r * Math.cos(angle);
          const y = node.y - r * Math.sin(angle);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
      } else {
        // Circle for others
        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      }

      ctx.fillStyle = color;
      ctx.fill();
      
      if (isHighlighted) {
        ctx.strokeStyle = "#1f2937";
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Draw name label
      ctx.fillStyle = "#374151";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(node.name.split(" ")[0], node.x, node.y + radius + 12);
    });

    ctx.restore();
  }, [filteredNodes, filteredEdges, zoom, hoveredNode, workerFilter, panOffset]);

  const roleStats = {
    champions: nodes.filter((n) => n.role === "champion").length,
    bridges: nodes.filter((n) => n.role === "bridge").length,
    learners: nodes.filter((n) => n.role === "learner").length,
    isolates: nodes.filter((n) => n.role === "isolate").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        No network data available. Workers need to check in and interact for network mapping.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Glossary / Term Explanations */}
      <Collapsible open={glossaryOpen} onOpenChange={setGlossaryOpen}>
        <Card className="p-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Understanding Network Analysis Terms</span>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${glossaryOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {TERM_EXPLANATIONS.map((item) => (
                <div key={item.term} className="p-3 bg-muted/50 rounded-lg">
                  <div className="font-medium text-sm text-foreground">{item.term}</div>
                  <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-success" />
          <span className="text-sm">Champion (★)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-primary" />
          <span className="text-sm">Bridge</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-warning" />
          <span className="text-sm">Learner</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-destructive" />
          <span className="text-sm">Isolate/High Risk</span>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <div className="w-8 h-0.5 bg-border" />
          <span className="text-sm">Supervisor</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 border-t-2 border-dashed border-border" />
          <span className="text-sm">Working together</span>
        </div>
      </div>

      {/* Filter Controls */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-muted-foreground mb-1 block">
              <Filter className="h-3 w-3 inline mr-1" />
              Connection Type
            </Label>
            <Select value={connectionTypeFilter} onValueChange={setConnectionTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by connection type" />
              </SelectTrigger>
              <SelectContent>
                {CONNECTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-muted-foreground mb-1 block">
              Select Worker
            </Label>
            <Select value={workerFilter} onValueChange={setWorkerFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Select a worker" />
              </SelectTrigger>
              <SelectContent>
                {workerOptions.map((worker) => (
                  <SelectItem key={worker.value} value={worker.value}>
                    {worker.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-3 w-3 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
        
        {hasActiveFilters && (
          <div className="mt-3 text-xs text-muted-foreground">
            Showing {filteredNodes.length} workers with {filteredEdges.length} connections
            {workerFilter !== "all" && ` for "${workerOptions.find(w => w.value === workerFilter)?.label}"`}
            {connectionTypeFilter !== "all" && ` (${CONNECTION_TYPES.find(t => t.value === connectionTypeFilter)?.label} only)`}
          </div>
        )}
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Champions</div>
          <div className="text-xl font-bold text-success">{roleStats.champions}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Bridges</div>
          <div className="text-xl font-bold text-primary">{roleStats.bridges}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Learners</div>
          <div className="text-xl font-bold text-warning">{roleStats.learners}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Isolates</div>
          <div className="text-xl font-bold text-destructive">{roleStats.isolates}</div>
        </Card>
      </div>

      {/* Canvas with zoom controls */}
      <div ref={containerRef} className={`relative ${isFullscreen ? "bg-background p-4 h-screen w-screen overflow-hidden" : ""}`}>
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <Button size="icon" variant="outline" onClick={() => setZoom((z) => Math.min(z + 0.2, 2))} title="Zoom in">
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="outline" onClick={() => setZoom((z) => Math.max(z - 0.2, 0.5))} title="Zoom out">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="outline" onClick={() => { setZoom(1); setPanOffset({ x: 0, y: 0 }); }} title="Reset view">
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="outline" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>

        <Card className="overflow-hidden">
          <canvas
            ref={canvasRef}
            width={600}
            height={isFullscreen ? 600 : 400}
            onClick={handleCanvasClick}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseLeave}
            className="w-full bg-muted/30 cursor-grab"
            style={{ maxHeight: isFullscreen ? "calc(100vh - 100px)" : "400px" }}
          />
        </Card>

        {/* Node Detail Dialog - inside fullscreen container */}
        <Dialog open={!!selectedNode} onOpenChange={() => setSelectedNode(null)}>
          <DialogContent className="z-[100]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedNode?.node.name}
                <Badge className={
                  selectedNode?.node.role === "champion" ? "bg-success" :
                  selectedNode?.node.role === "bridge" ? "bg-primary" :
                  selectedNode?.node.role === "isolate" ? "bg-destructive" : "bg-warning"
                }>
                  {selectedNode?.node.role}
                </Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Risk Score</div>
                  <div className="text-2xl font-bold">{selectedNode?.node.riskScore || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Centrality</div>
                  <div className="text-2xl font-bold">{(selectedNode?.node.centrality || 0).toFixed(2)}</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Connections ({selectedNode?.connections.length || 0})</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedNode?.connections.map((conn, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span>{conn.name}</span>
                      <Badge variant="outline">{conn.type}</Badge>
                    </div>
                  ))}
                  {selectedNode?.connections.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      No connections found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Click on a node to see connection details. Drag to pan. Solid lines = supervisor, Dashed lines = coworker.
      </p>
    </div>
  );
};
